import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useMemo, useReducer } from 'react';

import type {
    MaterialsOnTheFlyContextType,
    MaterialsOnTheFlyState,
} from './utils';
import type { ExtendedMaterialOnTheFly, MaterialOnTheFly } from '../../gql';
import type { MaterialOnTheFlyValue } from '../../types';

import { MaterialsOnTheFlyContext, materialsOnTheFlyReducer } from './utils';

interface MaterialsOnTheFlyProviderProps<
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
> {
    initialMaterialsOnTheFly?: MaterialsOnTheFlyState<T>;
}

/**
 * This provider will work either only with ExtendedMaterialOnTheFly or MaterialOnTheFly
 * @param initialMaterialsOnTheFly The initial materials on the fly (coming from sessions storage, existing BOM, etc.)
 */
export const MaterialsOnTheFlyProvider = <
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
>({
    initialMaterialsOnTheFly = [],
    children,
}: PropsWithChildren<MaterialsOnTheFlyProviderProps<T>>): ReactElement => {
    const [materialsOnTheFly, dispatchMaterialsOnTheFly] = useReducer(
        materialsOnTheFlyReducer<T>,
        initialMaterialsOnTheFly
    );

    const createMaterialOnTheFly = useCallback(
        (material: MaterialOnTheFlyValue) => {
            if (!material.materialSubcategory) {
                throw new Error('Material subcategory cannot be null.');
            }

            // Check if the MOF already exists in the state
            const doesMofExist = materialsOnTheFly.some(
                (mof) =>
                    mof.materialOnTheFlyPublicId ===
                    material.materialOnTheFlyPublicId
            );

            dispatchMaterialsOnTheFly({
                type: doesMofExist ? 'update' : 'create',
                payload: {
                    materialOnTheFlyValue: material,
                    materialSubcategory: material.materialSubcategory, // Guaranteed to be non-null
                },
            });
        },
        [materialsOnTheFly]
    );

    const contextValue = useMemo(
        (): MaterialsOnTheFlyContextType<T> => ({
            materialsOnTheFly,
            createMaterialOnTheFly,
        }),
        [materialsOnTheFly, createMaterialOnTheFly]
    );

    return (
        <MaterialsOnTheFlyContext.Provider value={contextValue}>
            {children}
        </MaterialsOnTheFlyContext.Provider>
    );
};
